import { useState } from "react";
import { Mail, Phone, Send, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { COMPANY } from "../../data/products";

const JOBS = [
  "Firma ogólnobudowlana",
  "Dekarz / hydroizolacja",
  "Monter fasad i dociepleń",
  "Instalator okien / drzwi",
  "Glazurnik / posadzkarz",
  "Inwestor prywatny",
  "Inne",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    job: "",
    message: "",
    rodo: false,
  });
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.rodo) {
      toast.error("Uzupełnij wszystkie wymagane pola i zaznacz zgodę RODO.");
      return;
    }
    const subject = encodeURIComponent(
      `[BUDOBET.PL] Zapytanie ofertowe — ${form.name}`
    );
    const body = encodeURIComponent(
      `Imię i nazwisko: ${form.name}\nEmail: ${form.email}\nTelefon: ${form.phone}\nZawód: ${form.job}\n\nWiadomość:\n${form.message}`
    );
    window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
    setSent(true);
    toast.success("Otworzyliśmy program pocztowy. Dziękujemy za zapytanie!");
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="bg-zinc-50 py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-black bg-white">
          {/* Left info panel */}
          <aside className="lg:col-span-5 bg-black text-white p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 h-1 w-32 bg-red-600" />
            <div className="flex items-center gap-3 mb-5">
              <span className="h-[2px] w-10 bg-red-500" />
              <span className="font-tech text-xs tracking-[0.3em] uppercase text-red-500 font-bold">
                07 · Kontakt
              </span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase leading-none tracking-tighter mb-6">
              Zapytaj<br />o cenę<span className="text-red-500">.</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-10">
              Wypełnij formularz lub skontaktuj się z nami bezpośrednio.
              Odpowiadamy zwykle w ciągu 24 godzin roboczych.
            </p>

            <ul className="space-y-5">
              <ContactItem
                icon={Phone}
                label="Telefon"
                value={COMPANY.phone}
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                testid="contact-info-phone"
              />
              <ContactItem
                icon={Mail}
                label="E-mail"
                value={COMPANY.email}
                href={`mailto:${COMPANY.email}`}
                testid="contact-info-email"
              />
            </ul>

            <div className="absolute bottom-0 left-0 right-0 h-2 bg-stripes" />
          </aside>

          {/* Right form */}
          <div className="lg:col-span-7 p-8 lg:p-12">
            {sent ? (
              <div
                className="flex flex-col items-start gap-4"
                data-testid="contact-success"
              >
                <CheckCircle2 className="h-14 w-14 text-red-600" strokeWidth={1.5} />
                <h3 className="font-display text-3xl uppercase tracking-tighter">
                  Dziękujemy!
                </h3>
                <p className="text-zinc-600 max-w-md">
                  Twoje zapytanie zostało przygotowane w programie pocztowym.
                  Skontaktujemy się w ciągu 24 godzin.
                </p>
                <Button
                  onClick={() => setSent(false)}
                  className="rounded-none mt-4 bg-black hover:bg-zinc-800 text-white"
                >
                  Wyślij kolejne zapytanie
                </Button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={submit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <FormField label="Imię i nazwisko *">
                    <Input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jan Kowalski"
                      className="rounded-none border-2 border-zinc-200 focus:border-red-600 h-12"
                      required
                    />
                  </FormField>
                  <FormField label="Adres e-mail *">
                    <Input
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="jan@firma.pl"
                      className="rounded-none border-2 border-zinc-200 focus:border-red-600 h-12"
                      required
                    />
                  </FormField>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <FormField label="Numer telefonu">
                    <Input
                      type="tel"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+48 600 000 000"
                      className="rounded-none border-2 border-zinc-200 focus:border-red-600 h-12"
                    />
                  </FormField>
                  <FormField label="Zawód / branża">
                    <Select value={form.job} onValueChange={(v) => update("job", v)}>
                      <SelectTrigger
                        data-testid="contact-job"
                        className="rounded-none border-2 border-zinc-200 h-12"
                      >
                        <SelectValue placeholder="Wybierz..." />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {JOBS.map((j) => (
                          <SelectItem key={j} value={j}>
                            {j}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>

                <FormField label="Treść wiadomości *">
                  <Textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Np. potrzebuję wycenę 250 m² wełny 0,035 grubości 150 mm..."
                    rows={5}
                    className="rounded-none border-2 border-zinc-200 focus:border-red-600 resize-none"
                    required
                  />
                </FormField>

                <label className="flex items-start gap-3 cursor-pointer pt-2">
                  <Checkbox
                    data-testid="contact-rodo"
                    checked={form.rodo}
                    onCheckedChange={(v) => update("rodo", v === true)}
                    className="rounded-none border-2 border-zinc-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 mt-1"
                  />
                  <span className="text-xs text-zinc-600 leading-relaxed">
                    Wyrażam zgodę na przetwarzanie moich danych osobowych przez
                    BUDOBET.PL w celu udzielenia odpowiedzi na zapytanie. Wiem,
                    że mogę wycofać zgodę w dowolnym momencie. *
                  </span>
                </label>

                <Button
                  type="submit"
                  data-testid="contact-submit"
                  className="rounded-none w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider px-10 h-14 text-base mt-4"
                >
                  <Send className="mr-2 h-5 w-5" /> Wyślij zapytanie
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="font-tech text-xs uppercase tracking-widest text-zinc-500 mb-2 block font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="h-10 w-10 border-2 border-red-500 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-red-500" />
      </div>
      <div>
        <div className="font-tech text-[10px] uppercase tracking-widest text-zinc-400 mb-0.5">
          {label}
        </div>
        <div className="font-display text-lg text-white" data-testid={testid}>
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <li>
      <a href={href} className="hover:text-red-400 transition block">
        {inner}
      </a>
    </li>
  ) : (
    <li>{inner}</li>
  );
}
